import { useState } from 'react'
import { ChevronDown, ChevronUp, Zap, Bell, Clock, AlertTriangle } from 'lucide-react'

interface SystemSMS {
  id: string
  name: string
  icon: React.ReactNode
  trigger: string
  template: string
  recipients: string
}

const SYSTEM_SMS_DEFINITIONS: SystemSMS[] = [
  {
    id: 'tg-decline',
    name: 'TG avsäger sig uppdrag',
    icon: <Bell className="h-5 w-5" />,
    trigger: 'När en trädgårdsmästare väljer "Avsäg" på ett uppdrag',
    template: 'TG {namn} har avsagt sig uppdrag #{uppdragsnr} ({titel}) planerat {datum}',
    recipients: 'Alla admins med SMS-notifikationer aktiverat'
  },
  {
    id: 'cron-error',
    name: 'Kronjobb misslyckas',
    icon: <AlertTriangle className="h-5 w-5" />,
    trigger: 'När ett schemalagt jobb (cron) misslyckas',
    template: 'VARNING: Kronjobb misslyckades! Job: {jobname}. Tid: {timestamp}',
    recipients: 'Alla admins med SMS-notifikationer aktiverat'
  },
  {
    id: 'cron-health',
    name: 'Daglig systemkontroll',
    icon: <Clock className="h-5 w-5" />,
    trigger: 'Dagligt kl 05:00 om status-jobbet inte körts på 48+ timmar',
    template: 'VARNING: Kronjobbet update-assignment-statuses har inte körts på 48+ timmar. Kontrollera systemet.',
    recipients: 'Alla admins med SMS-notifikationer aktiverat'
  }
]

export default function SystemSMSSection() {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="mt-8 border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
      {/* Header - Clickable to expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
            <Zap className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-purple-900 dark:text-purple-100">
              Automatiska SMS
            </h3>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              SMS som skickas automatiskt av systemet
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-purple-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-purple-500" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 bg-white dark:bg-nature-900 space-y-4">
          <p className="text-sm text-nature-600 dark:text-nature-400">
            Dessa meddelanden skickas automatiskt vid specifika händelser.
            De kan inte redigeras härifrån - de definieras i databasens funktioner.
          </p>

          <div className="space-y-4">
            {SYSTEM_SMS_DEFINITIONS.map((sms) => (
              <div
                key={sms.id}
                className="border border-nature-200 dark:border-nature-700 rounded-lg overflow-hidden"
              >
                {/* SMS Header */}
                <div className="flex items-center gap-3 p-3 bg-nature-50 dark:bg-nature-800">
                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                    {sms.icon}
                  </div>
                  <h4 className="font-medium text-nature-900 dark:text-nature-100">
                    {sms.name}
                  </h4>
                </div>

                {/* SMS Details */}
                <div className="p-3 space-y-3">
                  <div>
                    <span className="text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">
                      Trigger
                    </span>
                    <p className="text-sm text-nature-700 dark:text-nature-300">
                      {sms.trigger}
                    </p>
                  </div>

                  <div>
                    <span className="text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">
                      Mall
                    </span>
                    <pre className="mt-1 p-2 bg-nature-100 dark:bg-nature-800 rounded text-xs text-nature-700 dark:text-nature-300 whitespace-pre-wrap font-mono">
                      {sms.template}
                    </pre>
                  </div>

                  <div>
                    <span className="text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">
                      Mottagare
                    </span>
                    <p className="text-sm text-nature-700 dark:text-nature-300">
                      {sms.recipients}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Tips:</strong> För att aktivera/avaktivera SMS-notifikationer för en admin,
              gå till deras profil under Trädgårdsmästare och slå på/av "Ta emot SMS vid TG-avsägning".
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
